/**
 * Types and values for learner preferences in wizard, preference center.
 * See taxonomy id definitions in https://docs.google.com/spreadsheets/d/1lIr5_virIKbdMl8BLLc3T6ixcnFgcPVCsU498dhSFqg
 */

export enum Intent {
  Exploring = "bc78722c-e8f2-4f61-8c65-e833359bc1c4",
  NewUser = "7b996e70-d4b2-485b-afc1-787c62e7b0c1",
  NewSkills = "f2b97aa3-6056-42ca-8808-87fbf9d3728a",
  Certification = "6bce9822-01c2-4546-80f4-af7ae4811ca6",
  GetHelp = "21c95bc2-73e9-4f39-a47c-5b3cc0588609",
  GrowBusiness = "933deb87-6331-4772-bc1e-66f84a5b752b",
}

export enum Level {
  Beginner = "236ed5b9-a8de-4a6e-b49f-0eb612e982a5",
  Intermediate = "7064a9e8-5fd7-416b-a77a-dc1650e480c6",
  Advanced = "9fe4bb64-fcb9-415b-b29b-2335a844d54e",
}

export enum Solution {
  Sales = "sales",
  Data = "data",
  Automation = "automation",
  CustomerSuccess = "customer_success",
  Relationships = "relationships",
  Ai = "ai",
}

export enum Product {
  AccountEngagement = "42cffaf0-cc50-4a2e-bcc5-f98b361d3a1d",
  Agentforce = "d4e97755-9465-4769-9171-7e836b64e542",
  AppExchange = "42cffaf0-cc50-4a2e-bcc5-f98b361d3a1d",
  CodeBuilder = "694f7b9a-6baf-4180-90f1-ab0450d986c5",
  CommerceCloud = "244772c2-91a3-4b40-8097-a3a6d0064779",
  ConsumerGoodsCloud = "d8dbe19e-4636-4927-9a5e-d49222778802",
  CRMAnalytics = "a1b3d2e4-70ca-4f54-b69a-0bcbfd69cdf2",
  DataCloud = "cec01b75-03d2-4a53-9550-f3694cddf1f6",
  EducationCloud = "79559386-948d-4515-810e-508d761ecff6",
  Einstein = "240a9a11-caa3-4504-8891-ccc79db3d5b9",
  EinsteinCopilot = "d4e97755-9465-4769-9171-7e836b64e542",
  ExperienceCloud = "87af7cc3-4c43-4c9f-a4d7-5cef669311dc",
  FieldService = "a776b391-758c-4b86-b776-900acd186e7a",
  FinancialServicesCloud = "a869d73f-02f2-410c-ac2d-23c4aafa8777",
  FlowBuilder = "22a8ef62-668a-445d-bf6b-7e3330674d95",
  HealthCloud = "671cdf17-9811-4b89-95ad-0f6e46975091",
  IndustrySolutions = "ddc5a51e-af9f-426e-baaa-e1ea9aeb0de9",
  JourneyBuilder = "0dd7512f-2f02-45d0-9974-e408c23758ec",
  LoyaltyManagement = "bb43809d-a78d-4b28-a545-1d691bbfaec6",
  ManufacturingCloud = "370eda97-14c2-4dbd-9ee7-1c99e4810751",
  MarketingCloud = "7447d232-6ca2-4cc5-8432-6ff38bb0309e",
  MuleSoft = "a4660dec-95e1-40c3-bae4-3f6e3e1c7713",
  NetZeroCloud = "f500604f-f046-4aa4-bf0a-edf877af7292",
  NonprofitCloud = "4a51cee9-9968-4462-adf1-ca0b853eb121",
  PromptBuilder = "f4ce647b-6ee5-4801-be64-6a229a71451b",
  PublicSectorSolutions = "22d376e4-23c5-440e-beb2-af6883ce56a2",
  RevenueCloud = "11756165-4750-459f-bad3-8a117be7e7bc",
  SalesCloud = "753aac49-64c1-42ed-b92c-40aa56f8ff03",
  SalesforceMobileApp = "8f68eff8-e069-4cbe-a2ea-a34bda2d1342",
  SalesforcePlatform = "a4973f7d-37fb-4570-b8a7-a2c55cf49b46",
  ServiceCloud = "27c12145-271a-4de6-b6d3-2b6b08e78860",
  Slack = "ebda7954-c2a6-48e4-b962-71bd89c05551",
  Heroku = "3131ef7a-0ef3-4b6c-a96a-9fcffd581862",
  Tableau = "29b6c954-34f1-4612-a64b-1a9529580729",
}

export enum Role {
  Admin = "2dd12414-9ec7-467b-8adb-eaaad098bf4c",
  Architect = "678063dd-14fd-44ea-b6bb-ab7c63a7a929",
  BusinessAnalyst = "610598e8-fbb7-4bb5-91df-8a7afa9fb817",
  Consultant = "f6a32446-6213-4799-9572-91764ea0c31f",
  CustomerService = "53e2fe10-46c0-4233-8a9b-66b610a24fe0",
  Cybersecurity = "4f83bbad-1047-4449-af55-abc6fa565998",
  Designer = "b27e0179-8d01-4806-be88-7a4c8125c044",
  Developer = "e6c21a06-f7d3-493f-a4c6-7a79c1638d22",
  EndUser = "c5dd8b1d-eb29-43c9-b54a-9581e3b608cd",
  Marketer = "5cc3cbb0-e35a-434d-b2e5-08b1dbbd255f",
  Sales = "92772785-b8ef-4f0f-b488-9230dbaac18d",
}

export type Preferences = {
  intent?: Intent;
  solution?: Solution[];
  role?: Role[];
  product?: Product[];
  level?: Level;
  activeTrail?: string;
};

export interface HasPreferences {
  preferences: Preferences;
}

/**
 * Tell whether preferences object is empty.
 * @param prefs - preferences object.
 */
export function isPreferencesEmpty(prefs: Preferences) {
  return !Object.values(prefs).some((val) =>
    Array.isArray(val) ? !!val.length : !!val
  );
}
